<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { useAccount } from 'dashboard/composables/useAccount';
import { useStore } from 'dashboard/composables/store';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';
import AiCenterAPI from 'dashboard/api/aiCenter';
import { areaSlugForKind } from 'dashboard/routes/dashboard/aiCenter/itemLinks';
import { REPLY_EDITOR_MODES } from 'dashboard/components/widgets/WootWriter/constants';

const props = defineProps({
  item: { type: Object, required: true },
  conversationId: { type: Number, required: true },
});

const { t } = useI18n();
const route = useRoute();
const router = useRouter();
const { accountScopedRoute } = useAccount();
const store = useStore();
const busy = ref(false);
const error = ref(null);
const itemId = computed(() => props.item.item_id ?? props.item.itemId);
const itemState = ref({
  ...props.item,
  reason_summary: props.item.reason_summary ?? props.item.reasonSummary,
  suggested_message:
    props.item.suggested_message ?? props.item.suggestedMessage,
});
const canAct = computed(() =>
  ['pending', 'prepared', 'snoozed'].includes(itemState.value.status)
);
// D45: retomada ou imóvel com texto pronto aparece em balões no fim da
// conversa (AiCenterOpportunitySuggestion.vue), onde se envia, edita ou
// descarta. A nota fica só como aviso. Snooze, janela de anúncio (sai como
// modelo) e sugestão sem texto seguem com as ações aqui.
const noticeOnly = computed(() => {
  const state = itemState.value;
  return (
    ['opportunity', 'property'].includes(state.kind || props.item.kind) &&
    ['pending', 'prepared'].includes(state.status) &&
    state.payload?.source !== 'ad_window' &&
    Boolean(state.draft?.message)
  );
});

const refreshItem = async () => {
  try {
    const response = await AiCenterAPI.getItem(
      route.params.accountId,
      itemId.value
    );
    itemState.value = response.data?.data || response.data || itemState.value;
  } catch {
    // The note remains usable with its persisted snapshot if the detail endpoint is unavailable.
  }
};

const act = async action => {
  busy.value = true;
  error.value = null;
  try {
    const response = await AiCenterAPI.opportunityAction(
      route.params.accountId,
      itemId.value,
      action,
      {
        version: itemState.value.version ?? itemState.value.lock_version ?? 0,
        ...(action === 'snooze'
          ? { until: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString() }
          : {}),
      }
    );
    const item = response.data?.data || response.data;
    itemState.value = item || itemState.value;
    if (action === 'prepare_reply' && item?.draft?.message) {
      const conversationId =
        props.conversationId || route.params.conversation_id;
      await store.dispatch('draftMessages/set', {
        key: `draft-${conversationId}-${REPLY_EDITOR_MODES.REPLY}`,
        message: item.draft.message,
      });
      emitter.emit(BUS_EVENTS.PREPARE_CONVERSATION_REPLY, {
        conversationId,
        message: item.draft.message,
      });
    }
  } catch (requestError) {
    error.value = requestError;
  } finally {
    busy.value = false;
  }
};

// Abre direto a página do item na Central (`/v2/ai-center/retomadas/9`),
// sem passar pelo endereço antigo com `?tab=`. A resposta de uma ação pode
// vir sem `kind`: vale o da nota.
const openCenter = () =>
  router.push(
    accountScopedRoute(
      'ai_center_item',
      {
        area:
          areaSlugForKind(itemState.value.kind || props.item.kind) ||
          'retomadas',
        itemId: String(itemId.value),
      },
      { from_world: 'conversation' }
    )
  );

// Leva ao bloco de balões no fim da conversa (AiCenterOpportunitySuggestion).
const scrollToSuggestion = () => {
  const target = document.querySelector(
    `[data-testid="ai-center-opportunity-suggestion"][data-item-id="${itemId.value}"]`
  );
  if (target) target.scrollIntoView({ behavior: 'smooth', block: 'center' });
  else openCenter();
};

onMounted(refreshItem);
</script>

<template>
  <aside
    class="max-w-md rounded-lg border border-n-brand/30 bg-n-brand/5 px-3 py-2 text-left"
    data-testid="ai-center-private-note"
  >
    <div class="flex items-start gap-2">
      <span
        class="i-ph-sparkle size-4 shrink-0 text-n-brand"
        aria-hidden="true"
      />
      <div class="min-w-0 flex-1">
        <p class="m-0 text-xs font-bold uppercase tracking-wide text-n-brand">
          {{ t('AI_CENTER.PRIVATE_NOTE_TITLE') }}
        </p>
        <!-- Motivo em uma linha: o texto inteiro fica no title e na Central. -->
        <p
          class="mb-0 mt-1 truncate text-sm text-n-slate-12"
          :title="itemState.reason_summary || undefined"
          data-testid="ai-center-private-note-reason"
        >
          {{ itemState.reason_summary || t('AI_CENTER.NO_REASON') }}
        </p>
        <p
          v-if="noticeOnly"
          class="mb-0 mt-2 text-sm text-n-slate-11"
          data-testid="ai-center-private-note-below"
        >
          {{ t('AI_CENTER.CHAT_SUGGESTION.NOTE_BELOW') }}
        </p>
        <p
          v-else-if="itemState.draft?.message || itemState.suggested_message"
          class="mb-0 mt-2 line-clamp-3 text-sm text-n-slate-11"
        >
          {{ itemState.draft?.message || itemState.suggested_message }}
        </p>
      </div>
    </div>
    <p v-if="error" class="mb-0 mt-2 text-xs text-n-ruby-11" role="alert">
      {{ t('AI_CENTER.ACTION_ERROR') }}
    </p>
    <div v-if="noticeOnly" class="mt-1 flex flex-wrap gap-2">
      <button
        type="button"
        class="min-h-8 rounded-md bg-n-brand px-2.5 text-xs font-bold text-white"
        data-testid="ai-center-private-note-see"
        @click="scrollToSuggestion"
      >
        {{ t('AI_CENTER.CHAT_SUGGESTION.SEE_BELOW') }}
      </button>
      <button
        type="button"
        class="min-h-8 rounded-md px-2.5 text-xs font-bold text-n-brand hover:bg-n-brand/10"
        data-testid="ai-center-private-note-open"
        @click="openCenter"
      >
        {{ t('AI_CENTER.OPEN_IN_CENTER') }}
      </button>
    </div>
    <div v-else class="mt-3 flex flex-wrap gap-2">
      <button
        type="button"
        class="min-h-8 rounded-md bg-n-brand px-2.5 text-xs font-bold text-white disabled:opacity-50"
        :disabled="busy || !canAct"
        @click="act('prepare_reply')"
      >
        {{ t('AI_CENTER.PREPARE_REPLY') }}</button
      ><button
        type="button"
        class="min-h-8 rounded-md border border-n-weak px-2.5 text-xs font-bold text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
        :disabled="busy || !canAct"
        @click="act('snooze')"
      >
        {{ t('AI_CENTER.SNOOZE') }}</button
      ><button
        type="button"
        class="min-h-8 rounded-md border border-n-weak px-2.5 text-xs font-bold text-n-slate-12 hover:bg-n-alpha-2 disabled:opacity-50"
        :disabled="busy || !canAct"
        @click="act('dismiss')"
      >
        {{ t('AI_CENTER.DISMISS') }}</button
      ><button
        type="button"
        class="min-h-8 rounded-md px-2.5 text-xs font-bold text-n-brand hover:bg-n-brand/10"
        data-testid="ai-center-private-note-open"
        @click="openCenter"
      >
        {{ t('AI_CENTER.OPEN_IN_CENTER') }}
      </button>
    </div>
  </aside>
</template>

<style scoped>
button.border {
  border-style: solid;
}
</style>
